import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown } from 'lucide-react'
import {
    AppliesPrecision,
    TIME_BUCKETS,
    TimeBucket,
    bucketMidpoint,
    formatClock,
    minutesToAppliesAt,
    timeBucket,
    timeInputToAppliesAt,
    toTimeInputValue,
} from '../utils/dates'

/**
 * The one way to express a checkin's time of day.
 *
 * Extracted out of `CheckinComposer` so the time-assignment dialog and the
 * shortcut settings modal show the same control rather than inventing another
 * — one clock input, one bucket trigger and one set of four words in the
 * product, not several that can drift (`docs/checkin_time.md`).
 *
 * The contract is `applies_at` in, `applies_at` out: callers hand it whatever
 * the wire gave them (`"HH:MM:SS"`, `"HH:MM"` or `null`) and get back a value
 * fit to send straight back (`"HH:MM"` or `null`). **No time is `null`,
 * never `"00:00"`** — midnight is a time a user can deliberately set
 * and it renders `12:00a`, so the two spellings of "empty" mean opposite
 * things and `timeInputToAppliesAt` is where they are kept apart.
 *
 * **It grows with the precision the user asks for, and shrinks back as they
 * clear it.** With no time it is only the time-of-day words. Picking one adds
 * a blank clock beside it, to be more exact; typing a clock time fills it.
 * The words panel also offers "exact time", which skips the word: it opens
 * the clock blank and focused; a clock left blank goes away again. Any blank
 * clock has a "now" beside it, for the commonest exact time. Only
 * the most precise level carries a clear button, and clearing steps back one
 * level: the clock's goes back to the part of the day the time fell in (as
 * `approximate`, at that part's midpoint), the word's goes back to no time.
 * `inferred` shows as a clock time, like `exact` — it is one, just worked out.
 *
 * It reports **how** the value was produced alongside the value itself, and it
 * is the only control that can: `09:00` typed into the input and `09:00`
 * picked as "morning" are the same string, and nothing downstream could ever
 * tell them apart again. The two routes are already separate here — that is
 * the whole reason `exact` and `approximate` are expressible — so keeping them
 * separate is load-bearing rather than incidental tidiness.
 */
/**
 * How wide the words popover is allowed to get, in px.
 *
 * The four chips are about 330px laid out in one row, and the popover is
 * anchored by its **right** edge to the trigger that opens it — so on a phone,
 * where there is only ~240px to the left of that trigger, the row ran off the
 * left of the viewport with nothing to clip or scroll it: `night` was entirely
 * off-screen and `morning` was cut to "ning". Two chips a row fits inside the
 * narrowest phone this has to work on (320px) with room to spare, and wrapping
 * is what keeps the anchor honest — re-anchoring the popover would leave
 * `transformOrigin: 'top right'` pointing at nothing.
 *
 * It is applied as an inline style rather than a Tailwind class so the
 * constraint is readable from the DOM, and therefore assertable: jsdom loads
 * no stylesheet, so a `max-w-*` class would be a test of a string.
 */
export const BUCKET_POPOVER_MAX_WIDTH_PX = 192

export interface CheckinTimePickerProps {
    /** The current time, in any of the wire's spellings, or null for none. */
    appliesAt: string | null | undefined
    /** How that time was set, which decides how much of the picker shows.
     *  Unstated over a time is treated as a clock time. */
    precision?: AppliesPrecision | null
    /** The new `applies_at`, normalized to `"HH:MM"` or `null`, and how it was
     *  arrived at: `exact` from the clock input, `approximate` from a bucket
     *  word or from clearing the clock, `none` when the word is cleared. The picker never produces `inferred` —
     *  everything it emits is something the user did. */
    onChange: (appliesAt: string | null, precision: AppliesPrecision) => void
    /** Closes the words panel whenever the picker moves to a different
     *  subject — a different checkin in the composer. */
    resetKey?: string | number | null
    /** Appended to the controls' accessible names. Several pickers on one
     *  screen would otherwise all be called "Time of day"; the dialog passes
     *  the checkin they belong to. */
    ariaSuffix?: string
    /** Where the words panel pops from. Right-aligned by default, matching
     *  the composer, where the picker sits at the end of the date row. */
    bucketsAlign?: 'left' | 'right'
    className?: string
}

export default function CheckinTimePicker({
    appliesAt,
    precision,
    onChange,
    resetKey,
    ariaSuffix,
    bucketsAlign = 'right',
    className = '',
}: CheckinTimePickerProps) {
    // The four words live behind a toggle rather than sitting inline: the
    // composer's date row already holds two steppers and a date, and four more
    // controls would wrap it on a phone.
    const [showTimeBuckets, setShowTimeBuckets] = useState(false)
    // The clock asked for through "exact time" while there is no time yet. It
    // is only a view: nothing is set until a time is typed, and leaving it
    // blank puts the picker back to the words — on a click outside the picker,
    // not on blur, so that the "now" beside it can still be tapped.
    const [clockRequested, setClockRequested] = useState(false)
    useEffect(() => { setShowTimeBuckets(false); setClockRequested(false) }, [resetKey])
    const clockRef = useRef<HTMLInputElement>(null)

    const timeBucketsRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (timeBucketsRef.current && !timeBucketsRef.current.contains(event.target as Node)) {
                setShowTimeBuckets(false)
                setClockRequested(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => { document.removeEventListener('mousedown', handleClickOutside) }
    }, [])

    // `applies_at` arrives as `"HH:MM:SS"` from the server and as `"HH:MM"`
    // from the input; the input only takes the second form, so everything
    // goes through one normalizer.
    const currentBucket = timeBucket(appliesAt)
    // The three levels. A time with no stated precision is a clock time.
    const level: 'none' | 'word' | 'clock' =
        currentBucket === null ? 'none' : precision === 'approximate' ? 'word' : 'clock'
    // Under a word the clock is shown blank: the midpoint standing in for the
    // word is not a time anyone said, and showing it would read as one.
    const timeInputValue = level === 'clock' ? toTimeInputValue(appliesAt) : ''

    const label = (base: string) => (ariaSuffix ? `${base} ${ariaSuffix}` : base)

    // Typing a clock time is the user stating one, so it is `exact`. Emptying
    // the clock is a step back, not a wipe: the time drops to the part of the
    // day it fell in, which is the level below. `timeInputToAppliesAt` is what
    // keeps a cleared input from ever leaving as `"00:00"`, a real midnight.
    const handleTimeChange = (value: string) => {
        const typed = timeInputToAppliesAt(value)
        if (typed !== null) {
            onChange(typed, 'exact')
        } else if (level === 'clock' && currentBucket) {
            stepBackToWord(currentBucket)
        }
    }

    const stepBackToWord = (bucket: TimeBucket) => {
        onChange(bucketMidpoint(bucket), 'approximate')
    }

    // The four words are a shortcut for picking a time, not a stored kind:
    // each sets the middle of its range — morning 09:00, afternoon 15:00,
    // evening 21:00, night 03:00. What *is* remembered is that a word was
    // tapped at all — `approximate`, as against the clock's `exact` — which is
    // only knowable here, since `09:00` typed and `09:00` picked are one string
    // to everything downstream. Picking a word over a clock time is a step back
    // too, and the clock empties with it.
    //
    // The panel shuts on a pick: the clock that appears is the next thing to
    // reach for, and an open panel would sit on top of it.
    const pickBucket = (bucket: TimeBucket) => {
        setShowTimeBuckets(false)
        onChange(bucketMidpoint(bucket), 'approximate')
    }

    // "exact time": straight to the clock, focused. The clock is mounted and
    // focused inside the tap itself (`flushSync`) rather than from an effect
    // after it, because iOS only raises its time wheel for a focus that
    // happens during a user gesture.
    const openClock = () => {
        flushSync(() => {
            setShowTimeBuckets(false)
            setClockRequested(true)
        })
        clockRef.current?.focus()
    }
    const showClock = level !== 'none' || clockRequested
    // A blank clock offers the time it most often is — whether "exact time"
    // opened it or it sits blank beside a word. It is the user's own tap on a
    // clock reading, so `exact`, like typing it.
    const showNow = showClock && timeInputValue === ''
    const setNow = () => {
        const now = new Date()
        setClockRequested(false)
        onChange(minutesToAppliesAt(now.getHours() * 60 + now.getMinutes()), 'exact')
    }

    const clearButton = (key: string, title: string, onClick: () => void) => (
        <motion.button
            type="button"
            key={key}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            // Centred, not the chips' `left center`: it spins on hover, and
            // off-centre that swings it sideways.
            style={{ transformOrigin: 'center' }}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            onClick={onClick}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title={title}
            aria-label={label(title)}
        >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
        </motion.button>
    )

    return (
        <div className={`relative flex items-center gap-1 ${className}`} ref={timeBucketsRef}>
            {/* The words trigger reads the part of the day the time falls in,
                so it doubles as the readout of what is set. Amber while a word
                is the whole answer, matching the picked chip in the panel.
                Only the caret rotates; rotating the whole control would swing
                the word with it. `aria-label` stays fixed: the visible text
                varies with the value, the name says what the control does. */}
            <motion.button
                type="button"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                onClick={() => setShowTimeBuckets(open => !open)}
                className={`flex items-center gap-1 whitespace-nowrap px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-action ${
                    level === 'word'
                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                title="Pick a time of day"
                aria-label={label('Pick a time of day')}
                aria-expanded={showTimeBuckets}
                data-testid="time-bucket-trigger"
            >
                <span className={currentBucket ? '' : 'text-gray-500'}>
                    {currentBucket ?? 'time of day'}
                </span>
                <motion.span
                    animate={{ rotate: showTimeBuckets ? 180 : 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="flex"
                >
                    <ChevronDown className="h-4 w-4" />
                </motion.span>
            </motion.button>
            <AnimatePresence initial={false}>
                {level === 'word' && currentBucket &&
                    clearButton('clear-word', 'Clear time', () => onChange(null, 'none'))}
            </AnimatePresence>

            <AnimatePresence initial={false}>
                {showClock && (
                    <motion.input
                        key="clock"
                        ref={clockRef}
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        style={{ transformOrigin: 'left center' }}
                        type="time"
                        value={timeInputValue}
                        onChange={(e) => handleTimeChange(e.target.value)}
                        // A requested clock left blank was never a time. Focus
                        // moving elsewhere in the picker (to "now") keeps it;
                        // a click outside is caught by the listener above,
                        // since a tapped button takes no focus on iOS and so
                        // `relatedTarget` can't tell the two apart.
                        onBlur={(e) => {
                            const to = e.relatedTarget as Node | null
                            if (to && !timeBucketsRef.current?.contains(to)) setClockRequested(false)
                        }}
                        className="w-28 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                        aria-label={label('Time of day')}
                        title={timeInputValue ? `At ${formatClock(timeInputValue)}` : 'Set an exact time'}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {showNow && (
                    <motion.button
                        type="button"
                        key="now"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        style={{ transformOrigin: 'left center' }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        onClick={setNow}
                        className="whitespace-nowrap px-2 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-action"
                        title="Set to now"
                        aria-label={label('Set to now')}
                    >
                        now
                    </motion.button>
                )}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {level === 'clock' && currentBucket &&
                    clearButton('clear-clock', 'Clear exact time', () => stepBackToWord(currentBucket))}
            </AnimatePresence>

            <AnimatePresence>
                {showTimeBuckets && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        style={{
                            transformOrigin: bucketsAlign === 'right' ? 'top right' : 'top left',
                            maxWidth: BUCKET_POPOVER_MAX_WIDTH_PX,
                            flexWrap: 'wrap',
                        }}
                        data-testid="time-bucket-popover"
                        className={`absolute ${bucketsAlign === 'right' ? 'right-0' : 'left-0'} top-full mt-2 z-40 flex gap-1 rounded-md border border-gray-200 bg-white p-2 shadow-lg`}
                    >
                        <AnimatePresence>
                            {TIME_BUCKETS.map(bucket => (
                                <motion.button
                                    key={bucket.name}
                                    type="button"
                                    initial={{ opacity: 0, scale: 0.6 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.6 }}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                    style={{ transformOrigin: 'left center' }}
                                    onClick={() => pickBucket(bucket.name)}
                                    aria-pressed={currentBucket === bucket.name}
                                    className={`px-2 py-1 text-sm whitespace-nowrap rounded-full border ${
                                        currentBucket === bucket.name
                                            ? 'border-amber-300 bg-amber-100 text-amber-800'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    {bucket.name}
                                </motion.button>
                            ))}
                            <motion.button
                                key="exact"
                                type="button"
                                initial={{ opacity: 0, scale: 0.6 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.6 }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                style={{ transformOrigin: 'left center' }}
                                onClick={openClock}
                                aria-pressed={level === 'clock'}
                                className={`px-2 py-1 text-sm whitespace-nowrap rounded-full border ${
                                    level === 'clock'
                                        ? 'border-amber-300 bg-amber-100 text-amber-800'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                exact time
                            </motion.button>
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
